/**
 * TEMPORARY: Raw data audit page. Ignores all auth/session logic.
 * Fetches GET /api/debug/raw-expenses (no auth) to confirm if the frontend can see DB records.
 * Route: /debug-data
 */
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL ||
  'https://tax-expense-tracker-api.mr-doug-sullivan.workers.dev';

interface RawExpensesResponse {
  count: number | null;
  rows: Record<string, unknown>[];
  error?: string;
}

export default function DebugData() {
  const [data, setData] = useState<RawExpensesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetch(`${API_BASE_URL}/api/debug/raw-expenses`)
      .then((res) => res.json())
      .then((body: RawExpensesResponse) => {
        if (cancelled) return;
        if (body.error) {
          setError(body.error);
          setData({ count: body.count ?? null, rows: body.rows ?? [] });
        } else {
          setData(body);
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e?.message ?? 'Request failed');
          setData(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 text-white p-6">
        <p>Loading raw expense data (no auth)...</p>
      </div>
    );
  }

  const count = data?.count ?? 0;
  const rows = data?.rows ?? [];
  const columns = rows.length > 0 ? Object.keys(rows[0]) : [];

  return (
    <div className="min-h-screen bg-gray-900 text-white p-6 font-mono text-sm">
      <div className="max-w-full overflow-auto">
        <h1 className="text-2xl font-bold mb-4">Raw Data Audit (no auth)</h1>
        <p className="mb-2 text-amber-300">
          GET /api/debug/raw-expenses → COUNT(*) and SELECT * FROM expenses LIMIT 50
        </p>
        <p className="mb-4">
          <strong>Total rows in DB (expenses):</strong>{' '}
          <span className="text-green-400">{count}</span>
        </p>
        {error && (
          <p className="text-red-400 mb-4">Error: {error}</p>
        )}
        <p className="mb-4">
          <Link to="/login" className="text-blue-400 hover:underline">← Back to Login</Link>
          {' · '}
          <Link to="/" className="text-blue-400 hover:underline">Dashboard</Link>
        </p>
        <h2 className="text-lg font-bold mb-2">First 50 rows (raw)</h2>
        <div className="overflow-x-auto border border-gray-600 rounded">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gray-800">
                {columns.map((col) => (
                  <th key={col} className="border border-gray-600 px-2 py-1 text-left whitespace-nowrap">
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={i} className="bg-gray-800/50">
                  {columns.map((col) => (
                    <td key={col} className="border border-gray-600 px-2 py-1 whitespace-nowrap max-w-xs truncate">
                      {String((row as Record<string, unknown>)[col] ?? '')}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {rows.length === 0 && count === 0 && !error && (
          <p className="mt-4 text-amber-400">No rows returned. Table may be empty or query failed.</p>
        )}
      </div>
    </div>
  );
}
